import { useEffect, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Check, Loader2, WifiOff } from 'lucide-react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ColorPicker, GuestList } from '@/components/lobby/RoomPanel';
import { useRoom } from '@/hooks/useRoom';
import { isValidCode, normalizeCode, playerColorSchema } from '@/net/protocol';
import { readGuestMemo } from '@/net/room';
import type { JoinStage } from '@/net/roomStore';
import { getVariant, isVariantId } from '@/engine/variants';
import type { PlayerColorId } from '@/lib/options';
import { cn } from '@/lib/utils';
import { useSettings } from '@/store/settings';

const searchSchema = z.object({
  name: z.string().max(24).optional(),
  color: playerColorSchema.optional(),
});

export const Route = createFileRoute('/join/$code')({
  validateSearch: searchSchema,
  component: JoinPage,
});

const STAGES: { id: JoinStage; label: string }[] = [
  { id: 'relay', label: 'Reaching the relay' },
  { id: 'host', label: 'Waiting for the host' },
  { id: 'seat', label: 'Taking your seat' },
];
const ORDER: JoinStage[] = ['idle', 'relay', 'host', 'seat', 'done'];

function JoinPage() {
  const { code: rawCode } = Route.useParams();
  const search = Route.useSearch();
  const code = normalizeCode(rawCode);
  const room = useRoom();
  const navigate = useNavigate();
  const playerName = useSettings(s => s.playerName);
  const memo = readGuestMemo();
  const [name, setName] = useState(search.name ?? memo?.name ?? playerName);
  const [color, setColor] = useState<PlayerColorId>(
    search.color ?? memo?.color ?? 'magenta'
  );
  const [attempted, setAttempted] = useState(false);

  const connectedHere = room.role === 'client' && room.code === code;

  // Auto-join when arriving with a profile in the URL or a reclaimable session.
  useEffect(() => {
    if (attempted || connectedHere) return;
    const canAuto =
      (search.name && search.color) || (memo && memo.code === code);
    if (canAuto && isValidCode(code)) {
      setAttempted(true);
      void room
        .join(code, { name: name.trim() || 'Guest', color })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hand off to the game once the host starts.
  useEffect(() => {
    if (connectedHere && room.status === 'playing') {
      void navigate({ to: '/play' });
    }
  }, [connectedHere, room.status, navigate]);

  function join() {
    setAttempted(true);
    void room
      .join(code, { name: name.trim() || 'Guest', color })
      .catch(() => {});
  }

  if (!isValidCode(code)) {
    return (
      <Shell title="That code doesn't look right">
        <p className="text-sm text-muted">
          Room codes are five letters or numbers.
        </p>
        <Button variant="secondary" onClick={() => void navigate({ to: '/' })}>
          Back home
        </Button>
      </Shell>
    );
  }

  if (connectedHere && room.status === 'connecting') {
    return (
      <Shell title={`Joining ${code}`}>
        <JoinProgress stage={room.joinStage} hint={room.joinHint} />
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => room.retryConnection()}>
            Retry now
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              room.leave();
              void navigate({ to: '/' });
            }}
          >
            Leave
          </Button>
        </div>
      </Shell>
    );
  }

  if (
    connectedHere &&
    (room.status === 'waiting' || room.status === 'playing')
  ) {
    const variant =
      room.variantId && isVariantId(room.variantId)
        ? getVariant(room.variantId)
        : null;
    const away = room.connection !== 'connected' || !room.hostPresent;
    return (
      <Shell
        title={
          room.status === 'playing'
            ? `Back in ${room.hostName ?? 'the host'}'s game`
            : `Waiting for ${room.hostName ?? 'the host'} to start`
        }
      >
        {away && (
          <p
            role="status"
            className="flex items-center gap-2 rounded-[var(--radius)] border border-warn/40 bg-[color-mix(in_oklab,var(--warn)_12%,var(--surface))] px-3 py-2 text-sm text-warn"
          >
            <WifiOff className="h-4 w-4" />
            {room.connection !== 'connected'
              ? 'Reconnecting to the room…'
              : 'The host is away. Waiting for them to come back.'}
          </p>
        )}
        <p className="text-sm text-muted">
          {variant ? (
            <>
              Playing{' '}
              <span className="font-semibold text-text">{variant.name}</span>
              .{' '}
            </>
          ) : null}
          Keep this screen open; the game starts here automatically.
        </p>
        <GuestList
          seats={room.seats}
          selfId={room.selfPlayerId}
          emptyText="Seating you now."
        />
        <Button
          variant="ghost"
          onClick={() => {
            room.leave();
            void navigate({ to: '/' });
          }}
        >
          Leave room
        </Button>
      </Shell>
    );
  }

  if (connectedHere && (room.status === 'error' || room.status === 'ended')) {
    return (
      <Shell
        title={
          room.status === 'ended'
            ? 'The host closed the room'
            : 'Could not join'
        }
      >
        <p role="alert" className="text-sm text-danger">
          {room.error ?? 'Something went wrong.'}
        </p>
        <div className="flex gap-2">
          {room.status !== 'ended' && (
            <Button
              onClick={() => {
                room.clearError();
                join();
              }}
            >
              Try again
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              room.leave();
              void navigate({ to: '/' });
            }}
          >
            Back home
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title={`Join room ${code}`}>
      <form
        className="space-y-3"
        onSubmit={e => {
          e.preventDefault();
          join();
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="join-name">Your name</Label>
          <Input
            id="join-name"
            value={name}
            maxLength={24}
            autoFocus
            onChange={e => setName(e.target.value)}
          />
        </div>
        <ColorPicker value={color} onChange={setColor} />
        <div className="flex gap-2">
          <Button type="submit" disabled={name.trim().length === 0}>
            Join room
          </Button>
          <Link
            to="/"
            className="inline-flex h-10 items-center px-3 text-sm text-muted hover:text-text"
          >
            Back home
          </Link>
        </div>
      </form>
    </Shell>
  );
}

/** Three-step handshake with the current hint, so nobody stares at a bare spinner. */
export function JoinProgress({
  stage,
  hint,
}: {
  stage: JoinStage;
  hint: string | null;
}) {
  const idx = ORDER.indexOf(stage);
  return (
    <ol className="space-y-2" aria-label="Joining progress">
      {STAGES.map(s => {
        const sIdx = ORDER.indexOf(s.id);
        const done = idx > sIdx;
        const active = idx === sIdx;
        return (
          <li
            key={s.id}
            className={cn(
              'flex items-start gap-3 text-sm',
              done ? 'text-muted' : active ? 'text-text' : 'text-muted/60'
            )}
            aria-current={active ? 'step' : undefined}
          >
            <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center">
              {done ? (
                <Check className="h-4 w-4 text-accent-3" aria-hidden />
              ) : active ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <span className="h-2 w-2 rounded-full bg-line" aria-hidden />
              )}
            </span>
            <span className="flex flex-col">
              <span className="font-semibold">{s.label}</span>
              {active && hint && (
                <span role="status" className="text-warn">
                  {hint}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function Shell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <main className="mx-auto max-w-lg p-4 sm:p-6">
      <Card className="space-y-4 p-6">
        <h1 className="font-display text-2xl tracking-wide">{title}</h1>
        {children}
      </Card>
    </main>
  );
}
